// src/com/arrival_to_store/ArrivalToStore.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { Button, IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NearMeIcon from '@mui/icons-material/NearMe'; // 지도로 이동(특정차량)
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, CountLabel, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord, MapValue } from '../../value/AppConst';
import {
    arrivalToStoreListRepo, userInfoRepo, arrivalToStoreFilterRepo
} from '../../value/AppModel';
import { LIST_ARRIVAL_TO_STORE } from './ArrivalToStoreGql';
import { ArrivalToStoreDictionary } from './ArrivalToStoreDict';
import { useAlertSetter } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import ValueMisc from '../../value/ValueMisc';
import TransPlanReturnInput from '../trans_plan_return/TransPlanReturnInput';
import DateRangePicker from '../../common/DateRangePicker';
import AutoTextField from '../../common/AutoTextField';
import VzmSelector from '../../common/VzmSelector';
import { GET_TRANS_PLAN_RETURN } from '../trans_plan_return/TransPlanReturnGql';

const InputYnOptions = [
    {inputYn: AppWord.ALL, label: AppWord.AllKorean},
    {inputYn: 'Y', label: '입력'},
    {inputYn: 'N', label: '미입력'},
];

const TraceYnOptions = [
    {traceYn: AppWord.ALL, label: AppWord.AllKorean},
    {traceYn: 'Y', label: '가입'},
    {traceYn: 'N', label: '미가입'},
];

/**
 * (ARRIVAL_TO_STORE) 정보 View Controller
 * 특히 이 컴포넌트는 데이터 조회를 위해 복잡한 useEffect를 사용했음.
 * 향후 수정 시 주의 요망.
 * VIEW
 * 점포관리 (센터용)
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-12-23 10:33:03 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function ArrivalToStore() {
    const [duration, setDuration] = useState({dateFrom:'', dateTill:''});
    const [fetchDuration, setFetchDuration] = useState({dateFrom:null, dateTill:null}); // 데이터 조회를 한번만 하게 하려고 다소 복잡한 인수 전달 체계를 사용했음. ㅠㅠ.
    const sessionInfo = useReactiveVar(userInfoRepo);
    const arrivalToStoreListData = useReactiveVar(arrivalToStoreListRepo);
    const filter = useReactiveVar(arrivalToStoreFilterRepo);
    const [searchText, setSearchText] = useState('');
    const [countToDisplay, setCountToDisplay] = useState(0);
    const [arrivalToStoreSelected, setArrivalToStoreSelected] = useState([]);
    const [returnInputStatus, setReturnInputStatus] = useState({open:false, data:{}});
    const [storeOptions, setStoreOptions] = useState([{storeId:AppWord.ALL, storeName:AppWord.AllKorean}]);
    const [vehInfoOptions, setVehInfoOptions] = useState([{vehPlates:AppWord.ALL, label:AppWord.AllKorean}]);
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getArrivalToStoreList, responseList] = useLazyQuery(LIST_ARRIVAL_TO_STORE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: ArrivalToStoreDictionary.errorTitle.Get, message: [error.message]})
    });
    // 물류집기 입력을 위해 이 데이터를 가져옴
    const [getTransPlanReturnItem, responseTprItem] = useLazyQuery(GET_TRANS_PLAN_RETURN, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetTransPlanReturnItem(data, option)},
		onError: (error) => addAlert({title: '물류집기 정보 조회 오류', message: [error.message]})
	});

    const getArrivalToStoreListNow = (dateFrom, dateTill) => {
        const param = {variables:{dateFrom, dateTill}};
        getArrivalToStoreList(param);
        lastReloadRef.current = new Date().getTime()/1000;
    };

    // 다양한 조건을 고려하여 실제 데이터 호출 조건인 fetchDuration을 설정함.
    // fetchDuration값과 param값이 다르면 fetchDuration값을 변경하여 다른 useEffect를 호출함.
    // 이렇게 하지 않는 경우 getArrivalToStoreListNow가 세번씩 호출되는 문제가 있었음.
    useEffect(() => {
        if(!returnInputStatus.open) {
            let diff = new Date().getTime()/1000 - arrivalToStoreListData.when;
            if(diff > 300) {
                let from, till;
                if(arrivalToStoreListData.param && arrivalToStoreListData.param.dateFrom && arrivalToStoreListData.param.dateTill) {
                    from = arrivalToStoreListData.param.dateFrom;
                    till = arrivalToStoreListData.param.dateTill;
                }
                else {
                    from = ValueMisc.getYMD();
                    till = ValueMisc.getYMD();
                    const newData = {...arrivalToStoreListData, param:{dateFrom:from, dateTill:till}};
                    arrivalToStoreListRepo(newData);
                }
                setDuration({dateFrom:from, dateTill:till});
                if(fetchDuration.dateFrom !== from || fetchDuration.dateTill !== till) {
                    setFetchDuration({dateFrom:from, dateTill:till}); // trigger to get data
                }
            }
        }
    }, [arrivalToStoreListData, fetchDuration, returnInputStatus]);

    // 실제 데이터 조회를 위한 useEffect로서, fetchDuration이 변경되면 호출됨.
    useEffect(()=>{
        if(fetchDuration.dateFrom && fetchDuration.dateTill) {
            getArrivalToStoreListNow(fetchDuration.dateFrom, fetchDuration.dateTill);
        }
    }, [fetchDuration]);

    // 주 데이터 받으면 옵션 목록 만들기.
    useEffect(()=>{
        const vehMap = {};
        const vehList = [{vehPlates:AppWord.ALL, label:AppWord.AllKorean}];
        arrivalToStoreListData.records.forEach((rec)=>{
            if(rec.vehPlates && !vehMap[rec.vehPlates]) {
                vehList.push({vehPlates:rec.vehPlates, label:rec.vehPlates});
                vehMap[rec.vehPlates] = true;
            }
        });
        setVehInfoOptions(vehList);

        const storeMap = {};
        const storeList = [{storeId:AppWord.ALL, storeName:AppWord.AllKorean}];
        arrivalToStoreListData.records.forEach((rec)=>{
            if(rec.storeId && !storeMap[rec.storeId]) {
                storeList.push({storeId:rec.storeId, storeName:rec.storeName});
                storeMap[rec.storeId] = true;
            }
        });
        setStoreOptions(storeList);
    }, [arrivalToStoreListData]);

    // filter to criteria
    useEffect(()=>{
        const newCriteria = {};
        if(filter.storeId && filter.storeId !== AppWord.ALL) newCriteria.storeId = filter.storeId;
        if(filter.vehPlates && filter.vehPlates !== AppWord.ALL) newCriteria.vehPlates = filter.vehPlates;
        if(filter.traceYn && filter.traceYn !== AppWord.ALL) newCriteria.traceYn = filter.traceYn;
        if(filter.inputYn && filter.inputYn !== AppWord.ALL) newCriteria.inputYn = filter.inputYn;
        const newData = {...arrivalToStoreListData, criteria:newCriteria};
        arrivalToStoreListRepo(newData);
    }, [filter]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.arrivalToStoreList) {
            const newData = {...arrivalToStoreListData};
            newData.records = data.arrivalToStoreList.map((rec)=>{
                rec.inputYn = rec.inputBool ? 'Y' : 'N';
                rec.traceYn = rec.traceStatus ? 'Y' : 'N';
                return rec;
            });
            newData.when = new Date().getTime()/1000;
            arrivalToStoreListRepo(newData);
            // arrivalToStoreInputStatusRepo({open:false, data:{}});
            setReturnInputStatus({open:false, data:{}});
        }
    };

    const onCompleteGetTransPlanReturnItem = (data, option) => {
        if(data.transPlanReturnItem) {
            setReturnInputStatus({open:true, data:data.transPlanReturnItem});
        }
        else addAlert({title:'물류집기 정보 조회 오류', message:['지정한 PLAN을 찾을 수 없습니다.']});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else {
            setFetchDuration({dateFrom:arrivalToStoreListData.param.dateFrom, dateTill:arrivalToStoreListData.param.dateTill});
        }
    };

    const onChangeFrom = (ymd) => {
        setDuration({...duration, dateFrom:ymd});
        //lastReloadRef.current = 0;
    };

    const onChangeTo = (ymd) => {
        setDuration({...duration, dateTill:ymd});
        //lastReloadRef.current = 0;
    };

    const onClickGetList = () => {
        const newData = {...arrivalToStoreListData, param:{dateFrom:duration.dateFrom, dateTill:duration.dateTill}};
        if(duration.dateFrom !== fetchDuration.dateFrom || duration.dateTill !== fetchDuration.dateTill) {
            newData.when = 0; // useEffect에서 시간차 구할 때 300초 이상 차이가 나게 하기 위해.
        }
        arrivalToStoreListRepo(newData); // useEffect 2개가 동작해서 getArrivalToStoreListNow가 호출됨.
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onChangeFilter = (key, value) => {
        arrivalToStoreFilterRepo({...filter, [key]:value});
    };
    
    const onStoreSelected = (id, value) => {
        onChangeFilter(id, value);
    };

    const onChangeStoreInputText = (e, value, reason) => {
        if(reason === 'clear' || (reason === 'input' && value === '')) {
            onChangeFilter('storeId', AppWord.ALL);
        }
    };

    const onVehicleSelected = (id, value) => {
        onChangeFilter(id, value);
    };

    const onVehicleInputText = (e, value, reason) => {
        if(reason === 'clear' || (reason === 'input' && value === '')) {
            onChangeFilter('vehPlates', AppWord.ALL);
        }
    };

    const onSelectTraceStatus = (id, value) => {
        onChangeFilter(id, value);
    };

    const onSelectInputOrNot = (id, value) => {
        onChangeFilter(id, value);
    };

    const onClickRow = (row) => {
        // 한줄 출력을 위해 데이터 넣어 줌.
        setArrivalToStoreSelected([row]);
        // 물류집기 입력을 위해 이 데이터를 가져옴
        getTransPlanReturnItem({variables:{planId:row.planId}});
    };

    const onCompleteMutateReturnData = () => {
        // reload
        getArrivalToStoreListNow(fetchDuration.dateFrom, fetchDuration.dateTill);
    };

    const goToMap = (record) => {
        if(record.gpsTime) {
            const mapUrl = ValueMisc.makeMapUrl({
                job: MapValue.MapInitEvent.traceVehicle,
                vehInfoSeqToTrace:record.vehInfoSeq,
                vehPlates:record.vehPlates,
                vehAlias:record.vehPlates
            });
            window.open(mapUrl, AppWord.MAP_WINDOW_NAME);
        }
        else {
            addAlert({
                title: '위치 없음',
                message: ['위치정보가 없습니다. 위치정보가 있는 경우만 지도로 이동할 수 있습니다.']
            });
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
                    case AppObject.command.TO_MAP: goToMap(row); break;
            default: return;
        }
    };

    const tools = [
        {id:AppObject.command.TO_MAP, icon:<NearMeIcon color={AppStyle.mui.secondary}/>, where: (row)=>row.gpsTime},
    ];

    const loadingAny
        = responseList.loading
        || responseTprItem.loading;

    if(returnInputStatus.open) {
        const tableProps = {}; // deep copy
        tableProps.tableId = ArrivalToStoreDictionary.tableProps.tableId+'Selected';
        tableProps.columns = ArrivalToStoreDictionary.tableProps.columns;
        tableProps.uniqueKey = ArrivalToStoreDictionary.tableProps.uniqueKey;
        tableProps.flexOption = {...ArrivalToStoreDictionary.tableProps.flexOption};
        tableProps.flexOption.hideBubble = true;
        tableProps.flexOption.hideFirstTopButton = true;
        return(
            <ContentConainer>
                <Box style={{
                        display:'flex', flexDirection:'column', height:200,
                        width:'100%', boxSizing:'border-box'
                    }}
                >
                    <ContentHeader style={{marginTop:10}}>
                    물류집기 입력
                    </ContentHeader>
                    <FlexyTable
                        name={tableProps.tableId}
                        tableProps={tableProps}
                        records={arrivalToStoreSelected}
                    />
                </Box>
                <TransPlanReturnInput
                    open={returnInputStatus.open}
                    onClose={()=>setReturnInputStatus({open:false, data:{}})}
                    item={returnInputStatus.data}
                    onCompleteMutation={onCompleteMutateReturnData}
                />
            </ContentConainer>
        );
    }
    return(
        <ContentConainer>
            <ContentHeader>
                <ControlBox>
                    <DateRangePicker
                        dateFrom={duration.dateFrom}
                        dateTo={duration.dateTill}
                        onChangeFrom={onChangeFrom}
                        onChangeTo={onChangeTo}
                    />
                    <Button
                        startIcon={<CloudDownloadIcon />}
                        onClick={onClickGetList}
                        variant={AppStyle.mui.contained}
                        style={{marginLeft:10}}
                        disabled={loadingAny}
                    >
                        조회
                    </Button>
                </ControlBox>
                <ToolBox>
                    <CountLabel count={countToDisplay} />
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <ContentHeader>
                <ControlBox>
                    {
                        !sessionInfo.isStoreUser() &&
                        <AutoTextField
                            dict={{
                                id: 'storeId',
                                label: '도착지',
                                labelKey: 'storeName'
                            }}
                            value={filter.storeId || AppWord.ALL}
                            onChange={onStoreSelected}
                            onInputChange={onChangeStoreInputText}
                            list={storeOptions}
                            style={{minWidth:250, marginRight: 10}}
                        />
                    }
                    <AutoTextField
                        dict={{
                            id: 'vehPlates',
                            label: '차량',
                            labelKey: 'label'
                        }}
                        value={filter.vehPlates || AppWord.ALL}
                        onChange={onVehicleSelected}
                        onInputChange={onVehicleInputText}
                        fullWidth={true}
                        list={vehInfoOptions}
                        style={{marginRight:10, minWidth: 180}}
                    />
                    <VzmSelector
                        dict={{
                            id: 'traceYn', label: '위치추적', labelKey: 'label'
                        }}
                        value={filter.traceYn || AppWord.ALL}
                        list={TraceYnOptions}
                        onChange={onSelectTraceStatus}
                        style={{minWidth:110, marginRight:10}}
                    />
                    <VzmSelector
                        dict={{
                            id: 'inputYn', label: '시간등록', labelKey: 'label'
                        }}
                        value={filter.inputYn || AppWord.ALL}
                        list={InputYnOptions}
                        onChange={onSelectInputOrNot}
                        style={{minWidth:110, marginRight:10}}
                    />
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>

            </ContentHeader>
            <FlexyTable
                name={ArrivalToStoreDictionary.tableProps.tableId}
                tableProps={ArrivalToStoreDictionary.tableProps}
                records={arrivalToStoreListData.records}
                criteria={arrivalToStoreListData.criteria}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onChangeCountToDisplay={setCountToDisplay}
                onRowClicked={onClickRow}
            />
        </ContentConainer>
    );
}
